import type { ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasCamEditAccess } from '../../profile/utils/profileUtils';

/**
 * Route-level authorization guard for CAM Edit-gated claims routes (US4, FR-029).
 *
 * Mounts `children` ONLY once the profile has resolved AND the user has the **CAM Edit** claim,
 * so an unauthorized user's page shell/data hooks never mount (no partial rendering, no queue
 * query firing). Shows a neutral loading state while the profile is unresolved to avoid a
 * redirect flash; denies direct navigation otherwise.
 */
export function RequireCamEdit({ children }: { children: ReactElement }): ReactElement | null {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="claims-auth-loading" />
      </Box>
    );
  }

  if (!hasCamEditAccess(profile))
    return <Navigate to="/" replace />;

  return children;
}
